import React, { useEffect, useState } from 'react';
import { Badge, BlockStack, Box, Button, InlineStack, ProgressBar, Text } from '@shopify/polaris';
import { useDispatch } from 'react-redux';
import { useLanguage } from '../../../LanguageContext';
import { getProfile } from '../../../ducks/auth';
import * as api from '../../../services/api';
import ShopifyOnboardStep1 from './ShopifyOnboardStep1';
import ShopifyOnboardStep2 from './ShopifyOnboardStep2';
import ShopifyOnboardStep3 from './ShopifyOnboardStep3';
import './ShopifyOnboarding.css';

function ShopifyOnboarding({ onComplete }) {
  const dispatch = useDispatch();
  const { setLanguage, translate } = useLanguage();
  const lng = translate('shopifyOnboard');

  const [currentStep, setCurrentStep] = useState(1);
  const [animClass, setAnimClass] = useState('step-enter');

  const goToStep = (next) => {
    setAnimClass('step-exit');
    setTimeout(() => {
      setCurrentStep(next);
      setAnimClass('step-enter');
    }, 250);
  };

  //Step 1 state
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('language') || 'en');
  const [installLocation, setInstallLocation] = useState('0');
  const [themeOptions, setThemeOptions] = useState([]);
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [contactEmail, setContactEmail] = useState(localStorage.getItem('e') || '');

  const shopUserName = localStorage.getItem('shp_un');
  const progressValue = (currentStep / 3) * 100;

  //load profile data for pixel and plan pages
  useEffect(() => {
    if (localStorage.getItem('authToken')) {
      dispatch(getProfile());
    }
  }, []);

  //fetch shopify themes
  useEffect(() => {
    api.GET('/shopify/getThemes').then(res => {
      if (res.status === 'success' && res.data && res.data.length > 0) {
        const options = res.data.map(theme => ({
          label: theme.role === 'main' ? `${theme.name} (${lng.CurrentTheme || 'Current theme (default)'})` : theme.name,
          value: String(theme.id)
        }));
        //set current/main theme as default selected
        const mainTheme = res.data.find(theme => theme.role === 'main');
        if (mainTheme) {
          setInstallLocation(String(mainTheme.id));
        }
        setThemeOptions(options);
      }
    }).catch(() => { });
  }, []);

  const handleLngSelect = (val) => {
    setSelectedLanguage(val);
    setLanguage(val);
  }

  //Step 1 -> Step 2
  const handleStep1Continue = () => {
    if (window._cio) {
      window._cio.identify({
        id: localStorage.getItem('ti'),
        language: selectedLanguage,
        newsletter_opt_in: newsletterOptIn,
      });
      window._cio.track('Shopify_Onboard_Step1', {
        language: selectedLanguage,
        install_location: installLocation,
        newsletter_opt_in: newsletterOptIn,
      });
    }

    goToStep(2);
  };

  //Step 2 -> Step 3
  const handleStep2Continue = () => {
    if (window._cio) {
      window._cio.track('Shopify_Onboard_Step2');
    }
    goToStep(3);
  };

  //Step 3: Continue -> finish onboarding
  const handleStep3Continue = () => {
    if (window._cio) {
      window._cio.track('Shopify_Onboard_Complete');
    } 
    localStorage.setItem('shpf_onbrdng_done', 'true');
    onComplete();
  };

  return (
    <div className='shopi_onboard_bg'>
      {/* Header with step counter + progress */}
      <div className='shopify-onboard-header'>
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap={100}>
            <Text variant='headingLg' as='p' fontWeight='semibold' id='shopifyEmbedTextColor'>
              {lng.Hey} {shopUserName || 'Store Admin'}!
            </Text>
            <Text variant='headingXs' as='p' fontWeight='regular' tone='subdued'>
              {lng.SetupSubtitle}
            </Text>
          </BlockStack>
          <Badge tone="info">{(lng.StepOf || 'Step {step} of 3').replace('{step}', currentStep)}</Badge>
        </InlineStack>
        <Box paddingBlockStart={300}>
          <ProgressBar progress={progressValue} size="small" tone="primary" />
        </Box>
      </div>

      {/* Step content + nav grouped together for centering */}
      <div className={`shopify-onboard-step-content ${animClass}`}>
        {currentStep === 1 && (
          <ShopifyOnboardStep1
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={handleLngSelect}
            installLocation={installLocation}
            setInstallLocation={setInstallLocation}
            themeOptions={themeOptions}
            newsletterOptIn={newsletterOptIn}
            setNewsletterOptIn={setNewsletterOptIn}
            contactEmail={contactEmail}
            setContactEmail={setContactEmail}
            translate={lng}
          />
        )}
        {currentStep === 2 && <ShopifyOnboardStep2 themeId={installLocation} translate={lng} />}
        {currentStep === 3 && <ShopifyOnboardStep3 />}

        {/* Navigation buttons */}
        <div className='shopify-onboard-nav'>
          <InlineStack align={currentStep === 1 ? 'end' : 'space-between'}>
            {currentStep > 1 && (
              <Button onClick={() => goToStep(currentStep - 1)}>{lng.Back || 'Back'}</Button>
            )}
            <Button
              variant="primary"
              onClick={currentStep === 1 ? handleStep1Continue : currentStep === 2 ? handleStep2Continue : handleStep3Continue}
            >
              {currentStep === 3 ? (lng.Continue || 'Continue') : (lng.Next || 'Next')}
            </Button>
          </InlineStack>
        </div>
      </div>
    </div>
  );
}

export default ShopifyOnboarding;
